import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Plus, Upload, Pencil, Trash2, Eye, EyeOff, Boxes } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [currency, setCurrency] = useState('USD');
  const [manageStock, setManageStock] = useState(false);
  const [savingStockSetting, setSavingStockSetting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    category: '',
    stock: '0',
    track_stock: false,
    is_active: true,
  });

  useEffect(() => {
    fetchVendorTheme();
    fetchSettings();
    fetchProducts();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const response = await axios.get(`${API}/vendor/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setManageStock(!!response.data.manage_stock);
    } catch (error) {
      console.error('Failed to fetch store settings');
    }
  };

  const handleToggleManageStock = async (value) => {
    setSavingStockSetting(true);
    setManageStock(value); // optimistic
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(`${API}/vendor/profile`, { manage_stock: value }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(value ? 'Stock management enabled' : 'Stock management turned off');
    } catch (error) {
      setManageStock(!value); // revert
      toast.error('Could not update setting');
    } finally {
      setSavingStockSetting(false);
    }
  };

  const buildPayload = (data, product) => {
    // Stock is only tracked for an item when the store manages stock AND this
    // item opts in. Otherwise it's "always available" (kept effectively unlimited).
    const trackStock = manageStock && data.track_stock;
    return {
      name: data.name,
      description: data.description,
      price: parseFloat(data.price) || 0,
      image_url: data.image_url || '',
      category: data.category,
      track_stock: trackStock,
      stock: trackStock ? (parseInt(data.stock, 10) || 0) : (product ? product.stock : 9999),
      is_active: data.is_active !== false,
    };
  };

  const toggleActive = async (product) => {
    setTogglingId(product.id);
    try {
      const token = localStorage.getItem('vendor_token');
      await axios.put(`${API}/products/${product.id}`, {
        name: product.name,
        description: product.description,
        price: product.price,
        image_url: product.image_url || '',
        category: product.category,
        stock: product.stock,
        track_stock: product.track_stock || false,
        is_active: !product.is_active,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, is_active: !p.is_active } : p));
      toast.success(!product.is_active ? 'Item is now visible on your store' : 'Item hidden from your store');
    } catch (error) {
      toast.error('Could not update visibility');
    } finally {
      setTogglingId(null);
    }
  };

  const fetchVendorTheme = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const response = await axios.get(`${API}/vendor/theme`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.theme) {
        setCurrencySymbol(response.data.theme.currency_symbol || '₹');
        setCurrency(response.data.theme.currency || 'USD');
      }
    } catch (error) {
      console.error('Failed to fetch theme');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('vendor_token');

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, buildPayload(formData, editingProduct), {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, buildPayload(formData, null), {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Product created');
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;

    try {
      const token = localStorage.getItem('vendor_token');
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image_url: product.image_url || '',
      category: product.category,
      stock: (product.stock ?? 0).toString(),
      track_stock: product.track_stock === true,
      is_active: product.is_active !== false,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      image_url: '',
      category: '',
      stock: '0',
      track_stock: false,
      is_active: true,
    });
    setEditingProduct(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Image size must be less than 2MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('vendor_token');
      await axios.post(`${API}/products/upload-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Products uploaded successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to upload CSV');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Products</h2>
          <p className="text-muted-foreground mt-2">Manage your product catalog</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label htmlFor="csv-upload">
            <Button variant="outline" as="span" data-testid="upload-csv-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV
            </Button>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCSVUpload}
            />
          </label>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Product Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      data-testid="product-category-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    data-testid="product-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price ({currencySymbol})</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {currencySymbol}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      data-testid="product-price-input"
                      className="pl-8"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Currency: {currency} ({currencySymbol})
                  </p>
                </div>

                {/* Per-item stock tracking (only when the store manages stock) */}
                {manageStock && (
                  <div className="rounded-lg border p-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Boxes className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <Label className="cursor-default">Track stock for this item</Label>
                          <p className="text-xs text-muted-foreground">
                            Off = always available. On = set a quantity and show “Sold out” at 0.
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.track_stock}
                        onCheckedChange={(v) => setFormData({ ...formData, track_stock: v })}
                        data-testid="product-track-stock-switch"
                        aria-label="Track stock for this item"
                      />
                    </div>
                    {formData.track_stock && (
                      <div className="space-y-2">
                        <Label>Stock quantity</Label>
                        <Input
                          type="number"
                          value={formData.stock}
                          onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                          required
                          data-testid="product-stock-input"
                        />
                        <p className="text-xs text-muted-foreground">Set to 0 to mark as sold out</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Visibility */}
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    {formData.is_active ? <Eye className="h-4 w-4 text-muted-foreground" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                    <div>
                      <Label className="cursor-default">Show on storefront</Label>
                      <p className="text-xs text-muted-foreground">
                        {formData.is_active ? 'Customers can see and order this item' : 'Hidden from your store'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                    data-testid="product-active-switch"
                    aria-label="Show on storefront"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Product Image</Label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <label htmlFor="product-image-upload" className="flex-1">
                        <div className="flex items-center justify-center border-2 border-dashed rounded-lg p-4 hover:border-primary cursor-pointer transition-colors">
                          <Upload className="h-5 w-5 mr-2 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {uploadingImage ? 'Uploading...' : 'Click to upload image (Max 2 MB)'}
                          </span>
                        </div>
                        <input
                          id="product-image-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                        />
                      </label>
                    </div>
                    {formData.image_url && (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Product preview"
                          className="w-full h-40 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => setFormData({ ...formData, image_url: '' })}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="Or paste image URL"
                      data-testid="product-image-input"
                      className="text-xs"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="save-product-btn">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stock management setting */}
      <Card className="mb-6">
        <CardContent className="flex items-start sm:items-center justify-between gap-4 py-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2 mt-0.5 sm:mt-0">
              <Boxes className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium leading-tight">Manage stock</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Turn on to track inventory, then pick which products track stock from each item
                (everything else stays always-available). Leave off if you make items to order
                (e.g. a food cart) — no stock shown anywhere.
              </p>
            </div>
          </div>
          <Switch
            checked={manageStock}
            disabled={savingStockSetting}
            onCheckedChange={handleToggleManageStock}
            data-testid="manage-stock-switch"
            aria-label="Toggle stock management"
          />
        </CardContent>
      </Card>

      {products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">No products yet</p>
            <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-product-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className={`overflow-hidden transition-opacity ${product.is_active === false ? 'opacity-60' : ''}`}>
              {product.image_url && (
                <div className="relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  {product.is_active === false && (
                    <span className="absolute top-2 left-2 rounded-full bg-black/70 text-white text-xs font-medium px-2.5 py-1 flex items-center gap-1">
                      <EyeOff className="h-3 w-3" /> Hidden
                    </span>
                  )}
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-lg">{product.name}</CardTitle>
                <p className="text-sm text-muted-foreground">{product.category}</p>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">{currencySymbol}{product.price}</span>
                  {manageStock && product.track_stock && (
                    <span className={`text-sm ${product.stock <= 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                      {product.stock <= 0 ? 'Sold out' : `Stock: ${product.stock}`}
                    </span>
                  )}
                </div>

                {/* Show/hide on storefront */}
                <div className="flex items-center justify-between rounded-md border px-3 py-2 mb-3">
                  <span className="text-sm flex items-center gap-1.5">
                    {product.is_active === false
                      ? <><EyeOff className="h-3.5 w-3.5 text-muted-foreground" /> Hidden</>
                      : <><Eye className="h-3.5 w-3.5 text-muted-foreground" /> Visible</>}
                  </span>
                  <Switch
                    checked={product.is_active !== false}
                    disabled={togglingId === product.id}
                    onCheckedChange={() => toggleActive(product)}
                    data-testid={`toggle-active-${product.id}`}
                    aria-label="Show on storefront"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(product)}
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleDelete(product.id)}
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Products;
